import jwt_decode from 'jwt-decode'
import Vue from 'vue'
import getModuleUrl, { PORTAL_MODULE } from '@/helpers/getModuleUrl'

const getDefaultState = () => ({
    enableSessionExpiredDialog: false,
    exp: null,
    hash: '',
    iat: '',
    id: null,
    loadingAuth: false,
    modules: [],
    pending2FAEmail: '',
    reloadDialog: false,
    sessionExpiredDialog: false,
    status: '',
    system: '',
    token: '',
    user: {},
})

const state = getDefaultState()

const getters = {
    getEnableSessionExpiredDialog: (state) => state.enableSessionExpiredDialog,
    getHash: (state) => state.hash,
    getModules: (state) => state.modules,
    getSessionExpiredDialog: (state) => state.sessionExpiredDialog,
    getStatus: (state) => state.status,
    getToken: (state) => state.token,
    getTokenExp: (state) => state.exp,
    getUser: (state) => state.user,
    isAuthenticated: (state) => !!state.token,
    isLoadingAuth: (state) => state.loadingAuth,
}

const mutations = {
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setAuth(state, token) {
        state.token = token
        state.iat = jwt_decode(token).iat
        state.exp = jwt_decode(token).exp
    },

    setEnableSessionExpiredDialog(state, payload) {
        state.enableSessionExpiredDialog = payload
    },

    setHash(state, hash) {
        state.hash = hash
    },

    setInvalidHashDialog(state, payload) {
        state.invalidHashDialog = payload
    },

    setLoadingAuth(state, payload) {
        state.loadingAuth = payload
    },

    setModules(state, modules) {
        state.modules = modules
    },

    setPending2FAEmail(state, email) {
        state.pending2FAEmail = email || ''
    },

    setReloadDialog(state, payload) {
        state.reloadDialog = payload
    },

    setSessionExpiredDialog(state, payload) {
        state.sessionExpiredDialog = payload
    },

    setToken(state, token) {
        state.token = token
    },

    setTokenExp(state, exp) {
        state.exp = exp
    },

    setTokenIat(state, iat) {
        state.iat = iat
    },

    setUser(state, payload) {
        state.user = payload
    },
}

const actions = {
    async authenticate({ state, commit, dispatch }, user) {
        try {
            commit('setLoadingAuth', true)
            state.status = 'loading'

            const payload = {
                email: user.email,
                login_service: 'znap',
                password: user.password,
            }

            const res = await Vue.prototype.$http.post(
                `${Vue.prototype.$ipUser}user/login`,
                payload,
            )

            if (res?.data?.requires2FA) {
                commit('setPending2FAEmail', res.data.email || user.email)
                commit('setLoadingAuth', false)
                state.status = '2fa-required'

                return res
            }

            if (res) {
                commit('setHash', res.data.hash)
                dispatch('setClientModules')
                state.id = res.data.id[0]
                dispatch('setUser', state.id)
                commit('setAuth', res.data.token)
                commit('setLoadingAuth', false)
            }

            state.status = 'success'

            return res
        } catch (err) {
            commit('setLoadingAuth', false)
            state.status = 'error'
            throw err
        }
    },

    async getHashAuth({ state, commit, dispatch }, payload) {
        commit('setLoadingAuth', true)

        try {
            const res = await Vue.prototype.$http.post(`${Vue.prototype.$ipUser}user/get-hash`, {
                ...payload,
            })

            if (res.data.hash) {
                commit('setHash', res.data.hash)
                dispatch('setClientModules')
                state.id = res.data.id[0]
                dispatch('setUser', state.id)

                commit('setAuth', res.data.token)
                commit('setLoadingAuth', false)

                return 'success'
            }
            dispatch('logout')
            commit('setLoadingAuth', false)
        } catch (err) {
            commit('setLoadingAuth', false)
            console.log('error: ', err)

            return err
        }
    },

    async hashAuthenticate({ state, commit, dispatch }, hash) {
        commit('setHash', hash)
        commit('setLoadingAuth', true)

        try {
            const res = await Vue.prototype.$http.post(`${Vue.prototype.$ipUser}user/hash/login`, {
                hash,
            })

            if (res) {
                dispatch('setClientModules')
                state.id = res.data.id[0]
                dispatch('setUser', state.id)
                commit('setAuth', res.data.token)
                commit('setLoadingAuth', false)

                return 'success'
            }
        } catch (err) {
            commit('setLoadingAuth', false)
            console.log('error: ', err)

            return err
        }
    },

    async logout({ commit, dispatch }) {
        try {
            await Vue.prototype.$http.patch(`${Vue.prototype.$ipUser}user/update-hash`, {
                hash: null,
            })
        } catch (err) {
            console.log(err)
        } finally {
            commit('resetState')
            dispatch('redirectToLogin')
        }
    },

    redirectToLogin() {
        window.location.href = getModuleUrl(PORTAL_MODULE)
    },

    async setClientModules({ commit }) {
        const res = await Vue.prototype.$http.post(
            `${Vue.prototype.$ipClient}client-module/list/client`,
            {},
        )

        if (res) {
            const modules = res.data.rows.filter((r) => r.access_denied === 0)

            const portalIndex = modules.findIndex((m) => m.id_module === 1)

            modules.splice(portalIndex, 1)

            commit('setModules', modules)
        }
    },

    async setToken({ commit }, token) {
        commit('setToken', token)
        commit('setTokenIat', jwt_decode(token).iat)
        commit('setTokenExp', jwt_decode(token).exp)

        return token
    },

    async setUser({ commit }, userId) {
        try {
            const res = await Vue.prototype.$http.post(
                `${Vue.prototype.$ipUser}user/list/${userId}`,
                {},
            )

            if (res) {
                const user = res.data.rows[0]

                commit('setUser', user)
            }
        } catch (err) {
            this.$fnError(err)
        }
    },

    async verify2FA({ state, commit, dispatch }, { email, code }) {
        try {
            commit('setLoadingAuth', true)
            state.status = 'loading'

            const res = await Vue.prototype.$http.post(`${Vue.prototype.$ipUser}user/verify-2fa`, {
                code,
                email,
            })

            if (res) {
                commit('setHash', res.data.hash)
                commit('setPending2FAEmail', '')
                dispatch('setClientModules')
                state.id = res.data.id[0]
                dispatch('setUser', state.id)
                commit('setAuth', res.data.token)
                commit('setLoadingAuth', false)
            }

            state.status = 'success'

            return res
        } catch (err) {
            commit('setLoadingAuth', false)
            state.status = 'error'
            throw err
        }
    },
}

export default {
    actions,
    getters,
    mutations,
    state,
}
